import { Box } from "@mui/material";
import { styled } from "@mui/system";
import { cn } from "lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { supAvailabilitiesApiSlice } from "../../../api/availabilities/availabilitiesApiSlice";
import CloseIcon from "../../../components/icons/CloseIcon";
import { useApplyCoupon } from "../../../redux/flows/applyCouponFlow";
import { useCreateNewReservation } from "../../../redux/flows/createReservationFlow";
import { useFetchGroups } from "../../../redux/flows/fetchGroupsFlow";
import { useRemoveReservation } from "../../../redux/flows/removeReservationFlow";
import {
  addAnotherGroup,
  bookingCreationCompleted,
  bookingFlowChanged,
  bookingStepChanged,
  dateSelectedClear,
  durationSelectedClear,
  navigatedToNextStep,
  navigatedToPreviousStep,
  setHasSelectedLocation,
  setIsAddingAnotherEquipment,
  setNextEnabled,
  setSUPAvailabilities,
  availabilitiesLoadFailed,
  setSelectedLocation,
  setUseCase,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import { baseStore, RootState } from "../../../redux/store";
import routes from "../../../routes/routes";
import { usePrevious } from "../../../util/usePrevious";
import GeneralDialog from "../../common/GeneralDialog";
import Header from "../../layout/components/Header";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import TenantLogo from "../../layout/components/TenantLogo";
import BookLocationHeader from "../common/util/BookLocationHeader";
import BookLocationNavigateBackAction from "../common/util/BookLocationNavigateBackAction";
import BookLocationNavigateNextAction from "../common/util/BookLocationNavigateNextAction";
import ConfirmationStep from "./ConfirmationStep";
import EquipmentSelectionStep from "./EquipmentSelectionStep";
import UseCaseLocationSelectionStep from "../common/components/LocationSelectionStep/UseCaseLocationSelectionStep";
import { useNoBookableStation } from "../common/components/LocationSelectionStep/useNoBookableStation";
import OrderDataStep from "./OrderDataStep";
import PaymentStep from "./PaymentStep";
import PersonalDataStep from "./PersonalDataStep";
import { getSvcLocations } from "../../../util/utilFunctions";
import useServiceStorage from "../../../hooks/useServiceStorage";
import { ServiceTypeSelectorOption } from "../../../types/order";
import { useBookingWindow } from "../common/hooks/useBookingWindow";

export enum BookingCreationStep {
  locationSelection,
  assetGroupSelection,
  orderData,
  personalData,
  confirmation,
  paymentTypeSelection,
  payment,
}

const Root = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  color: "white",
  height: "100%",
  boxSizing: "border-box",
  backgroundColor: theme.palette.background.default,
  paddingBottom: `calc(${theme.spacing(1)} + env(safe-area-inset-bottom, 0))`,
  display: "flex",
  flexDirection: "column",
}));

const StepWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  width: "100%",
  maxWidth: 1000,
  margin: "0 auto",
  overflowY: "auto",
  paddingTop: theme.spacing(3),
  [theme.breakpoints.up("lg")]: {
    paddingTop: theme.spacing(6),
  },
}));

export const LogoBox = styled(Box)(({ theme }) => ({
  width: "150px",
  marginTop: theme.spacing(1),
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
}));

interface BookLocationDialogProps {
  returnPage: string;
}

const BookLocationPage = (props: BookLocationDialogProps) => {
  const stepWrapperRef = useRef<HTMLElement>();
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const bottomSentinelRef = useRef<HTMLDivElement | null>(null);
  const [pageDialogOpen, setPageDialogOpen] = useState<boolean>(false);
  const [atBottom, setAtBottom] = useState<boolean>(false);
  const [reservationError, setReservationError] = useState<boolean>(false);
  const dispatch = useDispatch();
  // The configured booking horizon for this use case — replaces the hardcoded
  // `add(1, "months").endOf("month")` this file computed inline. One month is
  // the DEFAULT here, so an unconfigured tenant requests exactly the same range
  // as before. `earliest` carries the lead buffer, which defaults to 0 for this
  // use case (the customer may book now), and is the same rule the app API
  // enforces (ADR-0020, `backend/src/core/lib/bookingWindow.ts`).
  const bookingWindow = useBookingWindow("sup");

  const { t } = useTranslation();
  const navigate = useNavigate();
  // const locationId = props.locationId;
  const {assetGroupId,
    orderData,
    step,
    appliedCouponCode,
    reservations,
    selectedLocation} = useSelector(
    (state: RootState) => state.bookingCreation
  );

  const previousStep = usePrevious(step);

const locations = useSelector((state: RootState) => state.booking.locations);
 const { updateService, } = useServiceStorage(
    "selectedService",
    ServiceTypeSelectorOption.SUP
  );
  const createNewReservation = useCreateNewReservation("sup", {
    throwOnError: true,
    sendsQuantity: true,
  });
  const applyCoupon = useApplyCoupon();
  const removeReservation = useRemoveReservation();
  const fetchGroups = useFetchGroups("sup");

  const paymentProvider = useSelector(
    (state: RootState) => state.bookingCreation.payment.provider
  );

  useEffect(() => {
    if (reservations.length === 0) {
      // dispatch(loadBookingFlow({}));
      stepWrapperRef.current = document.getElementById(
        "bookingProcessStepWrapper"
      )!;
      // @todo body scroll lock breaks the phone number input. Maybe remove it entirely?
      //disableBodyScroll(stepWrapperRef.current);
      //return () => enableBodyScroll(stepWrapperRef.current!);
      dispatch(
        bookingStepChanged({
          step: BookingCreationStep.locationSelection,
          nextEnabled: !!selectedLocation,
        })
      );

      // Set the usecase
      dispatch(setUseCase({ useCase: "sup" }));

      // Set the bookingFlow
      dispatch(
        bookingFlowChanged({
          bookingFlow: [
            "locationSelection",
            "assetGroupSelection",
            "orderData",
            "confirmation",
            "personalData",
            "paymentTypeSelection",
            "payment",
          ],
        })
      );
    }
  }, []);


  const fetchSUPAssetGroupAvailabilities = async (
    assetGroupId: string,
    from: Date,
    to: Date
  ) => {
    const data = await baseStore.dispatch(
      supAvailabilitiesApiSlice.endpoints.fetchAssetGroupSUPAvailabilities.initiate({
        assetGroupId: assetGroupId,
        from: from,
        to: to,
      })
    );
    return data;
  };

  useEffect(() => {
    if (
      reservations.length === 0 &&
      step === BookingCreationStep.confirmation
    ) {
      dispatch(bookingCreationCompleted());
    }
  }, [reservations]);



  useEffect(() => {
    const manageReduxData = async () => {
      if (step === BookingCreationStep.assetGroupSelection) {
        // Clean data
        dispatch(dateSelectedClear());
        dispatch(durationSelectedClear());
        await fetchGroups();
      } else if (
        step === BookingCreationStep.orderData &&
        assetGroupId &&
        previousStep &&
        !(previousStep > BookingCreationStep.orderData)
      ) {
        // TODO change to user input
        const fromDate = bookingWindow.earliest.toDate();
        const toDate = bookingWindow.latest.toDate();
        const { data: availabilities } = await fetchSUPAssetGroupAvailabilities(
          assetGroupId,
          fromDate,
          toDate
        );

        if (availabilities) {
          dispatch(setSUPAvailabilities(availabilities.availabilities));
        } else {
          // A failed load is shown as one, not as an endless loader (#2652 F2).
          dispatch(availabilitiesLoadFailed());
        }

      }
      // // previous step is orderData, current step is personal data
      if (
        previousStep === BookingCreationStep.personalData &&
        step === BookingCreationStep.confirmation
      ) {
        // Reset any prior error before retrying
        setReservationError(false);
        try {
          const processedReservation = await createNewReservation();
          updateService(ServiceTypeSelectorOption.SUP);

          if (processedReservation && appliedCouponCode) {
            // Due to asynchronous nature of redux; we add the returned processed reservation to previous state
            const reservationIds = reservations.map(
              (reservation: any) => reservation.reservationId!
            );
            reservationIds.push(processedReservation.reservationId!);
            await applyCoupon(reservationIds, appliedCouponCode);
          }
        } catch (error) {
          // createNewReservation rethrows on API failures (e.g. backend timeout).
          // Surface an inline error in ConfirmationStep so the user can navigate
          // back to personalData and retry instead of being stuck on the spinner.
          setReservationError(true);
        }
      }

      // // previous step is personal data, current step is board selection
      // // or previous step is personal data, current step is orderData
      if (
        step === BookingCreationStep.orderData &&
        assetGroupId &&
        previousStep &&
        previousStep > BookingCreationStep.orderData &&
        orderData.date // To ensure we are backing and not adding another sup
      ) {
        // user navigated back, so delete reservation
        await removeReservation();
        const fromDate = bookingWindow.earliest.toDate();
        const toDate = bookingWindow.latest.toDate();
        const { data: availabilities } = await fetchSUPAssetGroupAvailabilities(
          assetGroupId,
          fromDate,
          toDate
        );

        if (availabilities) {
          dispatch(setSUPAvailabilities(availabilities.availabilities));
        } else {
          // A failed load is shown as one, not as an endless loader (#2652 F2).
          dispatch(availabilitiesLoadFailed());
        }
      }

      if (
        previousStep === BookingCreationStep.personalData &&
        step === BookingCreationStep.confirmation &&
        paymentProvider &&
        paymentProvider.length > 1
      ) {
        // when there is more than one payment provider configured, we need to initialize payment type selection
        // dispatch(initializePaymentTypeSelection({}));
      }
      // previous step is payment summary, current step is payment
      // or previous step is payment type selection, current step is payment
      if (
        (previousStep === BookingCreationStep.confirmation &&
          step === BookingCreationStep.payment) ||
        (previousStep === BookingCreationStep.paymentTypeSelection &&
          step === BookingCreationStep.payment)
      ) {
        // dispatch(initializePaymentProvider({}));
      }

    };
    manageReduxData();
  }, [step]);

  // Every station closed (e.g. winter hibernation): the location step shows a
  // screen of its own, so the step title and "Weiter" step aside.
  const noBookableStation = useNoBookableStation("sup");
  const showClosedScreen =
    step === BookingCreationStep.locationSelection && noBookableStation;

  const headerText = useMemo(() => {
    switch (step) {
      case BookingCreationStep.locationSelection:
        return t("book_location.select_location");
      case BookingCreationStep.assetGroupSelection:
        return t("book_location.select_equipment");
      case BookingCreationStep.orderData:
        return t("book_location.order_data.header_text");
      case BookingCreationStep.confirmation:
        return t("book_location.confirmation.header_text");
      case BookingCreationStep.personalData:
        return t("book_location.personal_data.header_text");
      default:
        return "";
    }
  }, [step, orderData.date]);

  const isLoading = () => {
    if (
      step === BookingCreationStep.personalData ||
      step === BookingCreationStep.confirmation
    ) {
      return false;
    }

    return false;
  };

  const closePage = () => {
    // history.goBack();
    navigate(routes.home);
    dispatch(bookingCreationCompleted());
  };

  const handleAddAnotherEquipment = () => {
    dispatch(setIsAddingAnotherEquipment(true));
    dispatch(addAnotherGroup({}));
  };

  const handleReservationErrorRetry = () => {
    setReservationError(false);
    dispatch(navigatedToPreviousStep());
  };

  const isLastStep = step === BookingCreationStep.payment;

  // The sticky footer sits at z-10 above MUI Backdrop's z-index of 2 used by
  // LoaderOverlay. Without this guard the user could press "Next" while a step
  // is still loading (e.g. before POST /reservation completes on confirmation,
  // before equipment list is fetched, or before SUP availabilities arrive),
  // skipping ahead with incomplete state. Mirror each step's internal loading
  // condition here so the footer is hidden while the step shows its loader.
  const groupsByLocation = useSelector(
    (state: RootState) => state.bookingCreation.groupsByLocation
  );
  const supAvailabilities = useSelector(
    (state: RootState) => state.bookingCreation.supAvailabilities
  );
  const equipmentsForSelectedLocation = selectedLocation
    ? groupsByLocation[selectedLocation.id]
    : undefined;
  const isStepLoading =
    (step === BookingCreationStep.confirmation &&
      !(selectedLocation && reservations.length > 0)) ||
    (step === BookingCreationStep.assetGroupSelection &&
      !equipmentsForSelectedLocation?.length) ||
    (step === BookingCreationStep.orderData &&
      (!supAvailabilities || Object.keys(supAvailabilities).length === 0));

  // Observe whether the bottom sentinel (placed just before the sticky footer)
  // is visible inside the scrolling StepWrapper. If it is, the user has reached
  // the end of the content and the footer's top border is no longer needed as
  // a separator.
  useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    const root = scrollRootRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          setAtBottom(entry.isIntersecting);
        }
      },
      { root, threshold: 0, rootMargin: "0px 0px 1px 0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [step, isLastStep]);

  useEffect(() => {
    if (locations && step === 0) {
      const svcLocations = getSvcLocations(locations, 'sup');
      if (svcLocations.length === 1) {
        dispatch(navigatedToNextStep());
        dispatch(setSelectedLocation(svcLocations[0]));
        dispatch(setHasSelectedLocation(true));
        dispatch(setNextEnabled(false));
      };
    }
  }, [locations, step]);

  // When only one SUP location exists, locationSelection is auto-skipped (see
  // effect above), so assetGroupSelection becomes the effective first step
  // with no usable previous step. Hide the back button in that case to avoid
  // a non-functional control that would just re-trigger the auto-skip.
  const hasSingleSupLocation = useMemo(() => {
    if (!locations) return false;
    return getSvcLocations(locations, "sup").length === 1;
  }, [locations]);
  const hideBackButton =
    step === BookingCreationStep.assetGroupSelection && hasSingleSupLocation;
  return (
    <Root>
      <Header>
        <div className="h-12 absolute left-2 flex items-center">
          {!hideBackButton && <BookLocationNavigateBackAction />}
        </div>
        <TenantLogo />
        <button
          id="closeButton"
          aria-label={t("common.close", { defaultValue: "Close" })}
          className="size-12 absolute right-0 inline-flex items-center justify-center text-foreground/90 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() => setPageDialogOpen(true)}
        >
          <CloseIcon />
        </button>
      </Header>
      <Box sx={{ marginTop: "20px" }}>
        {!showClosedScreen && <BookLocationHeader text={headerText} />}
      </Box>
      <StepWrapper id={"bookingProcessStepWrapper"} ref={scrollRootRef}>
        {step === BookingCreationStep.locationSelection && (
          <UseCaseLocationSelectionStep service="sup" />
        )}
        {step === BookingCreationStep.assetGroupSelection && (
          <EquipmentSelectionStep />
        )}
        {step === BookingCreationStep.orderData && <OrderDataStep />}
        {step === BookingCreationStep.confirmation && (
          <ConfirmationStep
            onAddAnotherEquipment={handleAddAnotherEquipment}
            reservationError={reservationError}
            onRetry={handleReservationErrorRetry}
          />
        )}
        {step === BookingCreationStep.personalData && <PersonalDataStep />}
        {step === BookingCreationStep.payment && <PaymentStep />}
        {!isLastStep && (
          <div ref={bottomSentinelRef} aria-hidden={true} className="h-px" />
        )}
      </StepWrapper>
      {!isLastStep && !showClosedScreen && !reservationError && !isStepLoading && (
        <div
          className={cn(
            "sticky bottom-0 z-10 bg-background border-t max-w-[1000px] mx-auto w-full px-4 pt-6 transition-colors duration-150",
            atBottom ? "border-transparent" : "border-foreground/10"
          )}
          style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0))" }}
        >
          <BookLocationNavigateNextAction />
        </div>
      )}
      <LoaderOverlay open={isLoading()} />
      <GeneralDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        onContinue={closePage}
        content={t("use_order.closing_dialog.content")}
      />
    </Root>
  );
};

export default BookLocationPage;
